/**
 * Project color helpers.
 *
 * Project colors default to a deterministic value derived from the project id
 * (a golden-angle hue, so adjacent projects get visually distinct colors). A
 * project may also carry a custom `color` (a `#rrggbb` hex stored on the row);
 * when present it overrides the derived default everywhere the project's color
 * is shown (makers timeline fills, the swatch in the project name row, etc.).
 *
 * Keeping this in one module means the swatch that a user edits and the timeline
 * fill that renders always agree on a project's effective color.
 */

import type { CSSProperties } from "react";
import { hexToRgba } from "@/lib/role-formatting";

/** Stable non-negative hash of a project id (djb2-ish). */
export function hashProjectId(projectId: string | null): number {
  if (projectId === null) {
    return 0;
  }
  let hash = 0;
  for (let i = 0; i < projectId.length; i++) {
    const char = projectId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash | 0;
  }
  return Math.abs(hash);
}

/** Parse a `#rgb`/`#rrggbb` hex into an `[r, g, b]` tuple (0–255), or null. */
function hexToRgbTuple(hex: string): [number, number, number] | null {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized.split("").map((c) => c + c).join("")
      : normalized;
  if (expanded.length !== 6) return null;
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return [r, g, b];
}

/** Convert rgb (0–255) to HSL (h in [0,360), s/l in [0,1]). */
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rf = r / 255;
  const gf = g / 255;
  const bf = b / 255;
  const max = Math.max(rf, gf, bf);
  const min = Math.min(rf, gf, bf);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === rf) h = ((gf - bf) / d) % 6;
  else if (max === gf) h = (bf - rf) / d + 2;
  else h = (rf - gf) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return [h, s, l];
}

/**
 * Lighten a hex color by `deltaL` in HSL lightness space (clamped to [0,1]).
 * Used to build the slightly lighter second gradient stop.
 */
function lightenHex(hex: string, deltaL: number): string {
  const rgb = hexToRgbTuple(hex);
  if (!rgb) return hex;
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  return hslToHex(h, s, Math.min(1, Math.max(0, l + deltaL)));
}

/** Convert HSL (h in [0,360), s/l in [0,1]) to a `#rrggbb` hex string. */
function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp >= 0 && hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = l - c / 2;
  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * The default color for a project, derived from its id. Matches the hue math the
 * makers timeline used before colors were editable, so projects without a custom
 * color keep the exact same appearance.
 */
export function generateProjectHexColor(projectId: string | null): string {
  const hash = hashProjectId(projectId);
  const hue = (hash * 137.508) % 360; // golden-angle spacing
  return hslToHex(hue, 0.65, 0.55);
}

/** True when `value` is a `#rgb` or `#rrggbb` hex color string. */
export function isValidHexColor(value: string | null | undefined): value is string {
  return typeof value === "string" && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
}

/**
 * Normalize a user-typed/pasted hex string into a canonical lowercase
 * `#rrggbb`, or null if it isn't a valid 3- or 6-digit hex. Accepts values
 * with or without a leading `#` and surrounding whitespace (e.g. `A1B2C3`,
 * ` #a1b2c3 `, `abc`), so the hex input field can be forgiving.
 */
export function normalizeHexInput(raw: string): string | null {
  const trimmed = raw.trim().replace(/^#/, "");
  if (!/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) return null;
  const expanded =
    trimmed.length === 3
      ? trimmed.split("").map((c) => c + c).join("")
      : trimmed;
  return `#${expanded.toLowerCase()}`;
}

/** HSV color: h in [0,360), s/v in [0,1]. */
export interface Hsv {
  h: number;
  s: number;
  v: number;
}

/** Convert a `#rgb`/`#rrggbb` hex into HSV, or null when it isn't valid. */
export function hexToHsv(hex: string): Hsv | null {
  const rgb = hexToRgbTuple(hex);
  if (!rgb) return null;
  const rf = rgb[0] / 255;
  const gf = rgb[1] / 255;
  const bf = rgb[2] / 255;
  const max = Math.max(rf, gf, bf);
  const min = Math.min(rf, gf, bf);
  const d = max - min;
  const v = max;
  const s = max === 0 ? 0 : d / max;
  let h = 0;
  if (d !== 0) {
    if (max === rf) h = ((gf - bf) / d) % 6;
    else if (max === gf) h = (bf - rf) / d + 2;
    else h = (rf - gf) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s, v };
}

/** Convert HSV (h in [0,360), s/v in [0,1]) to a `#rrggbb` hex string. */
export function hsvToHex(h: number, s: number, v: number): string {
  const c = v * s;
  const hp = ((h % 360) + 360) % 360 / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp >= 0 && hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = v - c;
  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * The color a project actually renders with: its stored custom `color` when set
 * to a valid hex, otherwise the id-derived default.
 */
export function getEffectiveProjectColor(
  projectId: string | null,
  color: string | null | undefined,
): string {
  return isValidHexColor(color) ? color : generateProjectHexColor(projectId);
}

/**
 * The gradient fill style used for a project's timeline blocks: a 135° gradient
 * from the project's effective color at full opacity to a slightly lighter shade
 * of it at half opacity. The fully-opaque first stop makes the fill match the
 * color shown in the swatch; the second stop is lightened by 5% in HSL space and
 * faded to 0.5 alpha for a subtle gradient.
 */
export function getProjectFillStyle(
  projectId: string | null,
  color: string | null | undefined,
): CSSProperties {
  const hex = getEffectiveProjectColor(projectId, color);
  const color1 = hexToRgba(hex, 1);
  const color2 = hexToRgba(lightenHex(hex, 0.05), 0.5);
  return { background: `linear-gradient(135deg, ${color1}, ${color2})` };
}
